import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

export type DaypartingChartPoint = {
  hour: number;
  cpc: number | null;
  roas: number | null;
};

type DaypartingMetricsChartProps = {
  points: DaypartingChartPoint[];
  loading?: boolean;
  error?: boolean;
  subtitle?: string;
};

const CHART_WIDTH = 880;
const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 56, bottom: 44, left: 56 };
const PLOT_WIDTH = CHART_WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 72;
const TOOLTIP_GAP = 12;
const TOOLTIP_PAD = 4;

/** Place the tooltip beside the hovered point, flipping when it would clip the chart. */
function positionTooltip(pointX: number, pointY: number): { x: number; y: number } {
  const maxX = CHART_WIDTH - TOOLTIP_WIDTH - TOOLTIP_PAD;
  const maxY = CHART_HEIGHT - TOOLTIP_HEIGHT - TOOLTIP_PAD;

  let x = pointX + TOOLTIP_GAP;
  if (x > maxX) {
    x = pointX - TOOLTIP_GAP - TOOLTIP_WIDTH;
  }
  x = Math.min(maxX, Math.max(TOOLTIP_PAD, x));

  let y = pointY - TOOLTIP_HEIGHT - TOOLTIP_GAP;
  if (y < TOOLTIP_PAD) {
    y = pointY + TOOLTIP_GAP;
  }
  y = Math.min(maxY, Math.max(TOOLTIP_PAD, y));

  return { x, y };
}

function hourLabel(hour: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${suffix}`;
}

function formatCurrency(amount: number): string {
  return formatAccountCurrency(amount);
}

function scaleValue(value: number, min: number, max: number): number {
  if (max === min) return PLOT_HEIGHT / 2;
  const normalized = (value - min) / (max - min);
  return PLOT_HEIGHT - normalized * PLOT_HEIGHT;
}

type PathCoord = { x: number; y: number };

function sign(n: number): number {
  return n < 0 ? -1 : 1;
}

/** Tangent (dy/dx) at the middle of three points — d3 curveMonotoneX slope3. */
function monotoneSlope3(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const h0 = x1 - x0;
  const h1 = x2 - x1;
  const s0 = h0 === 0 ? 0 : (y1 - y0) / h0;
  const s1 = h1 === 0 ? 0 : (y2 - y1) / h1;
  const p = h0 + h1 === 0 ? 0 : (s0 * h1 + s1 * h0) / (h0 + h1);
  return (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
}

function monotoneSlope2(t: number, x0: number, y0: number, x1: number, y1: number): number {
  const h = x1 - x0;
  return h ? (3 * (y1 - y0) / h - t) / 2 : t;
}

/** Monotone cubic SVG path through the given coords (visual interpolation only). */
function monotoneCubicPath(coords: PathCoord[]): string {
  const n = coords.length;
  if (n === 0) return '';
  const start = `M${coords[0].x.toFixed(2)},${coords[0].y.toFixed(2)}`;
  if (n === 1) return start;
  if (n === 2) {
    return `${start} L${coords[1].x.toFixed(2)},${coords[1].y.toFixed(2)}`;
  }

  const tangents = new Array<number>(n);
  for (let i = 1; i < n - 1; i++) {
    tangents[i] = monotoneSlope3(
      coords[i - 1].x,
      coords[i - 1].y,
      coords[i].x,
      coords[i].y,
      coords[i + 1].x,
      coords[i + 1].y
    );
  }
  tangents[0] = monotoneSlope2(tangents[1], coords[0].x, coords[0].y, coords[1].x, coords[1].y);
  tangents[n - 1] = monotoneSlope2(
    tangents[n - 2],
    coords[n - 2].x,
    coords[n - 2].y,
    coords[n - 1].x,
    coords[n - 1].y
  );

  const parts = [start];
  for (let i = 0; i < n - 1; i++) {
    const x0 = coords[i].x;
    const y0 = coords[i].y;
    const x1 = coords[i + 1].x;
    const y1 = coords[i + 1].y;
    const dx = (x1 - x0) / 3;
    parts.push(
      `C${(x0 + dx).toFixed(2)},${(y0 + dx * tangents[i]).toFixed(2)} ${(x1 - dx).toFixed(2)},${(y1 - dx * tangents[i + 1]).toFixed(2)} ${x1.toFixed(2)},${y1.toFixed(2)}`
    );
  }
  return parts.join('');
}

function buildPath(
  points: DaypartingChartPoint[],
  metric: 'cpc' | 'roas',
  xForHour: (hour: number) => number,
  yForValue: (value: number) => number
): string {
  const segments: string[] = [];
  let current: PathCoord[] = [];

  const flush = () => {
    if (current.length) {
      segments.push(monotoneCubicPath(current));
      current = [];
    }
  };

  for (const point of points) {
    const value = point[metric];
    if (value === null || !Number.isFinite(value)) {
      flush();
      continue;
    }
    current.push({ x: xForHour(point.hour), y: yForValue(value) });
  }
  flush();
  return segments.join(' ');
}

type VisibleMetric = 'cpc' | 'roas';

export function DaypartingMetricsChart({
  points,
  loading = false,
  error = false,
  subtitle,
}: DaypartingMetricsChartProps) {
  const [hoveredHour, setHoveredHour] = useState<number | null>(null);
  const [visibleMetrics, setVisibleMetrics] = useState<Set<VisibleMetric>>(
    () => new Set(['cpc', 'roas'])
  );

  const showCpc = visibleMetrics.has('cpc');
  const showRoas = visibleMetrics.has('roas');

  const toggleMetric = (metric: VisibleMetric) => {
    setVisibleMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(metric)) {
        if (next.size === 1) return prev;
        next.delete(metric);
      } else {
        next.add(metric);
      }
      return next;
    });
  };

  const chartTitle =
    showCpc && showRoas
      ? 'CPC & ROAS by hour'
      : showCpc
        ? 'CPC by hour'
        : 'ROAS by hour';

  const scales = useMemo(() => {
    const cpcValues = points.map((p) => p.cpc).filter((v): v is number => v !== null && Number.isFinite(v));
    const roasValues = points.map((p) => p.roas).filter((v): v is number => v !== null && Number.isFinite(v));
    const cpcMin = cpcValues.length ? Math.min(...cpcValues) : 0;
    const cpcMax = cpcValues.length ? Math.max(...cpcValues) : 1;
    const roasMin = roasValues.length ? Math.min(...roasValues) : 0;
    const roasMax = roasValues.length ? Math.max(...roasValues) : 1;
    return {
      cpcMin,
      cpcMax: cpcMax === cpcMin ? cpcMin + 1 : cpcMax,
      roasMin,
      roasMax: roasMax === roasMin ? roasMin + 1 : roasMax,
    };
  }, [points]);

  const xForHour = (hour: number) => (hour / 23) * PLOT_WIDTH;
  const yForCpc = (value: number) => scaleValue(value, scales.cpcMin, scales.cpcMax);
  const yForRoas = (value: number) => scaleValue(value, scales.roasMin, scales.roasMax);

  const cpcPath = buildPath(points, 'cpc', xForHour, yForCpc);
  const roasPath = buildPath(points, 'roas', xForHour, yForRoas);

  const xTicks = [0, 3, 6, 9, 12, 15, 18, 21, 23];
  const cpcTicks = [scales.cpcMin, (scales.cpcMin + scales.cpcMax) / 2, scales.cpcMax];
  const roasTicks = [scales.roasMin, (scales.roasMin + scales.roasMax) / 2, scales.roasMax];

  const hoveredPoint = hoveredHour === null ? null : points.find((p) => p.hour === hoveredHour) ?? null;
  let tooltipPos: { x: number; y: number } | null = null;
  if (hoveredPoint) {
    const pointX = MARGIN.left + xForHour(hoveredPoint.hour);
    const ys: number[] = [];
    if (showCpc && hoveredPoint.cpc !== null && Number.isFinite(hoveredPoint.cpc)) {
      ys.push(MARGIN.top + yForCpc(hoveredPoint.cpc));
    }
    if (showRoas && hoveredPoint.roas !== null && Number.isFinite(hoveredPoint.roas)) {
      ys.push(MARGIN.top + yForRoas(hoveredPoint.roas));
    }
    const pointY = ys.length ? Math.min(...ys) : MARGIN.top + PLOT_HEIGHT / 2;
    tooltipPos = positionTooltip(pointX, pointY);
  }
  const hasData = points.some((p) => p.cpc !== null || p.roas !== null);

  return (
    <section className="bg-surface border border-border rounded-xl p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{chartTitle}</h4>
          {subtitle ? <p className="text-xs text-foreground-subtle mt-0.5">{subtitle}</p> : null}
        </div>
        <div
          className="inline-flex rounded-lg border border-border overflow-hidden"
          role="group"
          aria-label="Chart metrics"
        >
          <button
            type="button"
            onClick={() => toggleMetric('cpc')}
            aria-pressed={showCpc}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
              showCpc
                ? 'bg-orange-500 text-white'
                : 'bg-surface text-foreground-muted hover:bg-orange-50'
            }`}
          >
            <span
              className={`w-3 h-0.5 rounded ${showCpc ? 'bg-white' : 'bg-orange-500'}`}
              aria-hidden
            />
            CPC
          </button>
          <button
            type="button"
            onClick={() => toggleMetric('roas')}
            aria-pressed={showRoas}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-border ${
              showRoas
                ? 'bg-blue-600 text-white'
                : 'bg-surface text-foreground-muted hover:bg-blue-50'
            }`}
          >
            <span
              className={`w-3 h-0.5 rounded ${showRoas ? 'bg-white' : 'bg-blue-600'}`}
              aria-hidden
            />
            ROAS
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-foreground-subtle flex items-center gap-2 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-brand" />
          Loading chart…
        </p>
      ) : null}

      {!loading && error ? (
        <p className="text-sm text-error py-6">Failed to load chart data. Please try refresh.</p>
      ) : null}

      {!loading && !error && !hasData ? (
        <p className="text-sm text-foreground-subtle py-6">No hourly data for the last 3 days.</p>
      ) : null}

      {!loading && !error && hasData ? (
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            className="w-full min-w-[640px] h-auto"
            role="img"
            aria-label={`Line chart of ${chartTitle.toLowerCase()} for the last 3 days`}
          >
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {(showCpc ? cpcTicks : showRoas ? roasTicks : []).map((tick) => {
                const y = showCpc ? yForCpc(tick) : yForRoas(tick);
                return (
                  <g key={`grid-${tick}`}>
                    <line x1={0} y1={y} x2={PLOT_WIDTH} y2={y} stroke="rgb(229 231 235)" strokeWidth={1} />
                  </g>
                );
              })}

              {xTicks.map((hour) => {
                const x = xForHour(hour);
                return (
                  <g key={`x-${hour}`}>
                    <line x1={x} y1={0} x2={x} y2={PLOT_HEIGHT} stroke="rgb(243 244 246)" strokeWidth={1} />
                    <text
                      x={x}
                      y={PLOT_HEIGHT + 18}
                      textAnchor="middle"
                      className="fill-foreground-subtle text-[11px]"
                    >
                      {hourLabel(hour)}
                    </text>
                  </g>
                );
              })}

              {showCpc
                ? cpcTicks.map((tick) => (
                    <text
                      key={`cpc-label-${tick}`}
                      x={-10}
                      y={yForCpc(tick) + 4}
                      textAnchor="end"
                      className="fill-brand text-[10px]"
                    >
                      {formatCurrency(tick)}
                    </text>
                  ))
                : null}

              {showRoas
                ? roasTicks.map((tick) => (
                    <text
                      key={`roas-label-${tick}`}
                      x={PLOT_WIDTH + 10}
                      y={yForRoas(tick) + 4}
                      textAnchor="start"
                      className="fill-blue-700 text-[10px]"
                    >
                      {tick.toFixed(2)}
                    </text>
                  ))
                : null}

              {showCpc && cpcPath ? (
                <path d={cpcPath} fill="none" stroke="rgb(249 115 22)" strokeWidth={1.25} strokeLinejoin="round" />
              ) : null}
              {showRoas && roasPath ? (
                <path d={roasPath} fill="none" stroke="rgb(37 99 235)" strokeWidth={1.25} strokeLinejoin="round" />
              ) : null}

              {points.map((point) => {
                const x = xForHour(point.hour);
                return (
                  <rect
                    key={`hover-${point.hour}`}
                    x={x - PLOT_WIDTH / 48}
                    y={0}
                    width={PLOT_WIDTH / 24}
                    height={PLOT_HEIGHT}
                    fill="transparent"
                    onMouseEnter={() => setHoveredHour(point.hour)}
                    onMouseLeave={() => setHoveredHour(null)}
                  />
                );
              })}

              {hoveredHour !== null ? (
                <line
                  x1={xForHour(hoveredHour)}
                  y1={0}
                  x2={xForHour(hoveredHour)}
                  y2={PLOT_HEIGHT}
                  stroke="rgb(156 163 175)"
                  strokeDasharray="4 3"
                />
              ) : null}

              {showCpc
                ? points.map((point) => {
                    if (point.cpc === null) return null;
                    return (
                      <circle
                        key={`cpc-dot-${point.hour}`}
                        cx={xForHour(point.hour)}
                        cy={yForCpc(point.cpc)}
                        r={hoveredHour === point.hour ? 2.5 : 1.5}
                        fill="rgb(249 115 22)"
                      />
                    );
                  })
                : null}

              {showRoas
                ? points.map((point) => {
                    if (point.roas === null) return null;
                    return (
                      <circle
                        key={`roas-dot-${point.hour}`}
                        cx={xForHour(point.hour)}
                        cy={yForRoas(point.roas)}
                        r={hoveredHour === point.hour ? 2.5 : 1.5}
                        fill="rgb(37 99 235)"
                      />
                    );
                  })
                : null}
            </g>

            {hoveredPoint && tooltipPos ? (
              <foreignObject
                x={tooltipPos.x}
                y={tooltipPos.y}
                width={TOOLTIP_WIDTH}
                height={TOOLTIP_HEIGHT}
                className="pointer-events-none"
              >
                <div className="rounded-xl border border-border bg-white/95 px-2.5 py-1.5 text-xs shadow-sm">
                  <div className="font-medium text-foreground">{hourLabel(hoveredPoint.hour)}</div>
                  {showCpc ? (
                    <div className="text-brand">
                      CPC:{' '}
                      {hoveredPoint.cpc === null ? '—' : formatCurrency(hoveredPoint.cpc)}
                    </div>
                  ) : null}
                  {showRoas ? (
                    <div className="text-brand">
                      ROAS: {hoveredPoint.roas === null ? '—' : hoveredPoint.roas.toFixed(2)}
                    </div>
                  ) : null}
                </div>
              </foreignObject>
            ) : null}
          </svg>
        </div>
      ) : null}
    </section>
  );
}
